// types
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import {
  MembershipDataByTokenMVaultErrorResponse,
  MembershipDataByTokenMVaultSuccessResponse,
  MembershipSettingsParams,
  MembershipSettingsResponse
} from "@/lib/types/api/mvault";

// lib
import { isEmpty } from "@/lib/helpers/empty";
import { isValidDevMembershipToken } from "@/lib/helpers/is-valid-dev-membership-token";
import { MVAULT_ENDPOINT } from "@/lib/constants";
import { MVaultError } from "@/lib/errors";
import { unactivatedDevMembershipData } from "@/lib/mvault/mock";
import safeFetch from "@/lib/helpers/safe-fetch";
import { vaultFetch } from "@/lib/fakes/vault-fetch";
import { PassportTransferMembership } from "@/lib/types/passportTransferMembership";

const username = process.env.MVAULT_ACCESS_KEY!
const password = process.env.MVAULT_ACCESS_KEY_SECRET!

const credentials = btoa(`${username}:${password}`)
const authHeader = `Basic ${credentials}`

async function membershipByUID(uid: string) {
  const route = `${MVAULT_ENDPOINT}/memberships/by_uid/${uid}/`
  return await get(route)
}

interface ByTokenOptions {
  /**
   * Include memberships MVault considers inactivatable: disabled, expired, or
   * already activated.
   *
   * By default by_token answers 410 for those, so an activation flow gets
   * `errors: 'already active'`
   */
  includeInactivatable?: boolean;
}

/**
 * by_token answers 302 to /memberships/{id}/
 */
async function membershipDataByToken(token: string, options: ByTokenOptions = {}) {
  if(isValidDevMembershipToken(token)) {
    return unactivatedDevMembershipData
  }

  const query = options.includeInactivatable ? '?include_all_memberships=1' : ''
  const route = `${MVAULT_ENDPOINT}/memberships/by_token/${token}/${query}`
  const json = await get(route)

  if(json?.errors) {
    return json as MembershipDataByTokenMVaultErrorResponse
  } else {
    return json as MembershipDataByTokenMVaultSuccessResponse
  }
}

async function cancelMembership(mvaultId: string) {
  const route = `${MVAULT_ENDPOINT}/memberships/by_mvault_id/${mvaultId}/cancel_recurring_payment/`
  return await post(route)
}

async function activateMembership(stationId: string, membershipPk: number, uid: string) {
  const route = `${MVAULT_ENDPOINT}/memberships/${membershipPk}/`
  const json = await patch(route, { uid })

  if(isEmpty(json)) {
    return { activated: true }
  } else {
    return { activated: false, ...json }
  }
}

interface TransferTarget {
  stationCallSign: string;
  membershipId: string;
  pk: number;
  uid: string;
}

/**
 * Moves a membership onto another uid.
 *
 * MVault will not overwrite a uid in place, so this unsets it and then sets the
 * new one, using two different route forms because only these work for each step.
 * @returns { activated: boolean, ...json }
*/
async function transferMembership({ stationCallSign, membershipId, pk, uid }: TransferTarget) {
  const station_call_sign = stationCallSign;
  const membership_id = membershipId;
  const pid = uid;

  // For some reason, *this* form of the mvault API route is what works, not the one we use
  // when activating memberships. Go figure.
  const unsestMembershipRoute = `${MVAULT_ENDPOINT}/${station_call_sign}/memberships/${membership_id}/`

  // The mvault API will not let you simply overwrite a membership's uid.
  // https://docs.pbs.org/space/MV/5799969/Membership+Vault+API#Invalid-Payload-1
  // We need a two step process.
  // 1) We need to unset the membership's uid.
  const body = {
    uid: '',
    activation_date: null,
  }

  // This is the actual call to mvault.
  const unsetMembershipUidJson = await patch(unsestMembershipRoute, body)

  // Successfulls to mvault result in an empty json response.
  // If we get an empty response, we can proceed to step two.
  // If we get something in the JSON response, that means something went wrong.
  if(isEmpty(unsetMembershipUidJson)) {
    // Sign. We have to use *this* form of the API route when setting the uid,
    // because this form is the only one that well deactivate other memberships.
    const setMembershipRoute = `${MVAULT_ENDPOINT}/memberships/${pk}/`
    // 2) Set the membership with the uid of the signed in user.
    const bodyWithNewUid = {
      uid: pid
    }

    // This is the second call to mvault, where we set the new uid.
    const setNewMembershipUidJson = await patch(setMembershipRoute, bodyWithNewUid)

    // Again, an empty response means success.
    if(isEmpty(setNewMembershipUidJson)) {
      // Success!
      return { activated: true }
      } else {
      return { activated: false, ...setNewMembershipUidJson }
    }
  } else {
    return { activated: false, ...unsetMembershipUidJson }
  }
}

async function isPassportMember(uid: string) {
  const json = await membershipByUID(uid)

  if(Array.isArray(json)) {
    return json.some(m => m?.current_state?.has_access)
  }

  if(json?.errors) {
     if(json.errors === "No memberships exist for this user") {
      return false
     } else {
      throw new MVaultError(json.errors)
     }
  }

  return false
}

async function getActiveMembershipsByUID(uid: string) {
  const json = await membershipByUID(uid)
  let activeMemberships: PassportTransferMembership[] = [];

  if(Array.isArray(json)) {
    activeMemberships = json.filter(m => m?.current_state?.has_access)
  }

  if(json?.errors) {
     if(json.errors === "No memberships exist for this user") {
      return []
     } else {
      throw new MVaultError(json.errors)
     }
  }

  return activeMemberships;
}

async function getMembershipSettings(params: MembershipSettingsParams): Promise<MembershipSettingsResponse | null> {
  const { pid, isSVP, email, stationId, status } = params
  const searchParams = new URLSearchParams()
  // For SVP's we want to filter to the localized station, so we pass filter_station_id
  // For .org we only want to sort memberships with the current station passed - sort_station_id
  const sortOrFilterParam = isSVP ? 'filter_station_id' : 'sort_station_id';

  searchParams.set('pid', pid)
  if(email) searchParams.set('email', email)
  if(status) searchParams.set('status', status)
  if(stationId) searchParams.set(sortOrFilterParam, stationId)

  const route = `${MVAULT_ENDPOINT}/memberships/membership_settings/?${searchParams.toString()}`;

  // vaultFetch, not safeFetch: on staging/local this route can be answered by
  // the in-process fake scenarios for users who opted in via Dev Management.
  const response = await vaultFetch(route, {
    headers: {
      Authorization: authHeader
    },
    cache: 'no-store'
  })

  // 204: user has zero membership records. 404: user has records but none are
  // currently displayable (status=off or past grace period). Both map to the
  // same UX (Donate & Start Watching) — verified against staging MVault.
  if(response.status === 204 || response.status === 404) {
    return null
  }

  if(!response.ok) {
    throw new MVaultError(`MVault membership_settings returned ${response.status}`)
  }

  const json = await response.json()

  if(json?.errors) {
    throw new MVaultError(typeof json.errors === 'string' ? json.errors : JSON.stringify(json.errors))
  }

  return json as MembershipSettingsResponse
}

async function get(route: string) {
  const response = await safeFetch(route, {
    headers: {
      Authorization: authHeader
    },
    cache: 'no-store'
  })

  return await response.json()
}

async function post(route: string, body?: object) {
  const options: RequestInit = {
    method: 'POST',
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
    cache: 'no-store'
  }

  if(body) {
    options.body = JSON.stringify(body)
  }

  const response = await vaultFetch(route, options)

  return await response.json()
}

async function patch(route: string, body: object) {
  const response = await safeFetch(route, {
    method: 'PATCH',
    headers: {
      Authorization: authHeader,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body),
    cache: 'no-store'
  })

  if(response.ok && response.headers.get('content-length') === '0') {
    return {}
  }

  return await response.json()
}

export {
  cancelMembership,
  activateMembership,
  transferMembership,
  getActiveMembershipsByUID,
  getMembershipSettings,
  isPassportMember,
  membershipByUID,
  membershipDataByToken,
}
